
import { supabase } from '@/lib/supabase';
import { MessageType } from '@/types/assistant';
import { logger } from '@/utils/production-logger';
import { MessageValidationService } from './MessageValidationService';

export class MessagePersistenceService {
  /**
   * Verify that the session exists
   */
  static async verifySessionExists(sessionId: string, userId: string): Promise<boolean> {
    console.log('[PERSISTENCE] Checking if session exists:', { sessionId, userId });
    
    const { data: sessionExists, error: sessionError } = await supabase
      .from('assistant_sessions')
      .select('id')
      .eq('id', sessionId)
      .eq('user_id', userId)
      .single();

    if (sessionError || !sessionExists) {
      console.error('[PERSISTENCE] Session does not exist or access denied:', {
        sessionId,
        userId,
        error: sessionError?.message
      });
      logger.error('Session does not exist or access denied', {
        sessionId,
        userId,
        error: sessionError?.message,
        component: 'MessagePersistenceService'
      });
      return false;
    }

    console.log('[PERSISTENCE] Session exists, proceeding with message save');
    return true;
  }

  /**
   * Save message to database using UPSERT
   */
  static async saveMessageToDatabase(message: MessageType): Promise<boolean> {
    try {
      console.log('[PERSISTENCE] Starting save for message:', {
        messageId: message.id,
        sessionId: message.session_id,
        userId: message.user_id,
        role: message.role,
        contentLength: message.content?.length || 0,
        isStreaming: message.is_streaming
      });

      // Validate message
      const validation = MessageValidationService.validateMessage(message);
      if (!validation.isValid) {
        return false;
      }

      // Skip streaming messages
      if (MessageValidationService.shouldSkipSave(message)) {
        return true;
      }

      // Verify session exists
      const sessionExists = await this.verifySessionExists(message.session_id, message.user_id);
      if (!sessionExists) {
        return false;
      }

      // Clean message data
      const cleanMessageData = MessageValidationService.cleanMessageData(message);

      console.log('[PERSISTENCE] Clean message data prepared:', {
        messageId: cleanMessageData.id,
        sessionId: cleanMessageData.session_id,
        role: cleanMessageData.role
      });

      logger.debug('Saving validated message', {
        messageId: cleanMessageData.id,
        sessionId: cleanMessageData.session_id,
        component: 'MessagePersistenceService'
      });

      // STEP 2: Use UPSERT instead of INSERT + UPDATE fallback
      console.log('[PERSISTENCE] Using UPSERT for conflict-free save');
      const { error } = await supabase
        .from('assistant_messages')
        .upsert(cleanMessageData, {
          onConflict: 'id',
          ignoreDuplicates: false
        })
        .select()
        .single();

      if (error) {
        console.error('[PERSISTENCE] Database upsert error:', {
          error: error.message,
          errorCode: error.code,
          messageId: message.id
        });
        logger.error('Database upsert error', {
          error: error.message,
          messageId: message.id,
          component: 'MessagePersistenceService'
        });
        return false;
      }

      console.log('[PERSISTENCE] Message saved successfully via UPSERT:', { messageId: message.id });
      logger.debug('Message saved successfully', {
        messageId: message.id,
        component: 'MessagePersistenceService'
      });

      return true;
    } catch (error) {
      console.error('[PERSISTENCE] Unexpected save error:', {
        error: error instanceof Error ? error.message : String(error),
        messageId: message.id
      });
      logger.error('Unexpected save error', {
        error: error instanceof Error ? error.message : String(error),
        messageId: message.id,
        component: 'MessagePersistenceService'
      });
      return false;
    }
  }
}
